import { AuthService, AUTH_EVENT } from '@apparel/core/auth';
import { isGameBreaker } from '@apparel/core/platform';
import { cx } from '@apparel/react/cx';
import { useLoading } from '@apparel/react/hooks/useLoading';
import { useMounted } from '@apparel/react/hooks/useMounted';
import { useService } from '@apparel/react/hooks/useService';
import IconButton, { IconButtonProps } from '@material-ui/core/IconButton';
import makeStyles from '@material-ui/core/styles/makeStyles';
import React, { useCallback, useEffect, useState } from 'react';
import { CartEmitter, CartEvents } from './CartEmitter';
import { CartIcon } from './CartIcon';
import { CartService } from './CartService';

export interface CartButtonProps extends Omit<IconButtonProps, 'size'> {
    loading?: boolean;
    size?: number | string;
    itemsCount?: number;
    className?: string;
    ButtonProps?: {
        component?: any;
        icon?: any;
    };
}

export const CartButton = React.memo(
    React.forwardRef((props: CartButtonProps, ref: any) => {
        const { loading, className, size = 40, itemsCount, ButtonProps = {}, ...rest } = props;
        const classes = useStyle({ size });
        const [count, setCount] = useState(itemsCount || 0);
        const [requestLoading, requestLoadingActions] = useLoading();

        const isMounted = useMounted();

        const cart$ = useService(CartService);
        const auth$ = useService(AuthService);

        const handleUpdateCount = useCallback(async () => {
            requestLoadingActions.start();

            try {
                const count = await cart$.countItems();
                if (isMounted()) {
                    setCount(count);
                }
            } catch (e) {
                if (isMounted()) {
                    setCount(0);
                }
            }

            requestLoadingActions.stop();
        }, [setCount]);

        useEffect(() => {
            const offAuthLogin = auth$.on(AUTH_EVENT.LOGIN, handleUpdateCount);
            const offAdd = CartEmitter.on(CartEvents.ADD, handleUpdateCount);
            const offRemove = CartEmitter.on(CartEvents.REMOVE, handleUpdateCount);
            const offClear = CartEmitter.on(CartEvents.CLEAR, handleUpdateCount);

            // noinspection JSIgnoredPromiseFromCall
            handleUpdateCount();

            return () => {
                offAuthLogin();
                offAdd();
                offRemove();
                offClear();
            };
        }, []);

        rest.ref = ref;

        const iconClass = isGameBreaker() ? cx('cart-icon', classes.gbcart) : 'cart-icon';

        const { component: Component = IconButton, icon, ...buttonRest } = ButtonProps || {};

        return (
            <Component className={cx(classes.root, className)} {...rest} {...buttonRest}>
                <CartIcon icon={icon} loading={requestLoading || loading} itemsCount={count} className={iconClass} />
            </Component>
        );
    }),
);

CartButton.displayName = 'CartButton';

const useStyle = makeStyles(
    () => ({
        root: {
            position: 'relative',
            height: ({ size }: CartButtonProps) => size,
            width: ({ size }: CartButtonProps) => size,
        },

        gbcart: {
            color: '#8d8d8d !important',
        },

        loader: {
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            alignContent: 'center',
            transform: 'translate3d(-50%, -50%, 0)',
            top: '50%',
            left: '50%',
            zIndex: 3,
        },
    }),
    {
        name: 'CartButton',
    },
);

export default CartButton;
